<template xmlns="http://www.w3.org/1999/html">
    <form
        method=""
        action=""
        class="form-group"
        @submit.prevent="onSubmit"
        @keydown="getCurrentForm().errors.clear($event.target.name)"
        ref="form"
        autocomplete="off"
    >
        <div id="loader" v-if="isLoading">
            <div class="spinner"></div>
        </div>

        <component
            :is="{...currentStepComponent}"
            ref="currentStepRef"
            :step="currentStep"
            :fields="currentStepFields"
            :form-data="props.session"
            @button-updated="updateButtonColor"
            v-on:buttonDisabled="setIsDisabled"
            @input="handleError"
        ></component>

        <div class="form__buttons form-group py-3">
            <button class="btn btn-primary" data-cy="btn-next" :disabled="isDisabled">
                {{ currentStepNumber === totalSteps ? "Submit" : "Next" }}
            </button>
        </div>
        <p
            class="form-error"
            role="alert"
            aria-live="assertive"
            v-if="getCurrentForm && getCurrentForm.errors?.get('application-status')"
        >
            {{ getCurrentForm.errors.get("application-status") }}
        </p>
        <div v-if="supportMessage !== null">
            <h3>{{ supportMessage.title }}</h3>
            <div v-html="supportMessage.items[0]" />
        </div>
    </form>
</template>

<script setup>
import StepUploadID from "./DocumentUpload/StepUploadID";
import StepEussShare from "./DocumentUpload/StepEussShare.vue";
import StepSelfie from "./DocumentUpload/StepSelfie.vue";
import StepProofAddress from "./DocumentUpload/StepProofAddress.vue";
import StepBenefit from "./DocumentUpload/StepBenefit.vue";
import StepRightToWork from "./DocumentUpload/StepRightToWork.vue";
import StepBenefitQuestion from "./DocumentUpload/StepBenefitQuestion.vue"
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

const props = defineProps({
    steps: Array,
    course: String,
    session: Object,
    supportMessage: Object
})

const currentStepRef = useTemplateRef('currentStepRef')
const benefitsFiles = ref()
const previousStepNumber = ref(currentStepNumber)
const totalSteps = props.steps.length
const formData = ref(Array(totalSteps))
const formRef = useTemplateRef('form')
const isLoading = ref(false)
const isDisabled = ref(false)
const currentStepNumber = ref(1)
let currentStepComponent = ref()

let stepIdToComponentMappings = {
    uploadID: StepUploadID,
    benefit: StepBenefit,
    rightToWork: StepRightToWork,
    proofAddress: StepProofAddress,
    euss: StepEussShare,
    selfie: StepSelfie,
    benefitQuestion: StepBenefitQuestion,
};

window.onpopstate = (event) => {
    loadStepFromUrl();
};

loadStepFromUrl();

watch(currentStepNumber, () => {
    currentStep.value = props.steps[currentStepNumber.value - 1]
})

let currentStep = ref(props.steps[currentStepNumber.value - 1])

let stepComponents = computed(() => {
    return props.steps.map((step) => {
        return stepIdToComponentMappings[step.id];
     });
})

currentStepComponent = computed(() => {
    return stepComponents.value[currentStepNumber.value - 1]
}) 

const currentStepFields = computed(() => {
    let originalFields = currentStep.value.fields;
    
    for (const fieldName in originalFields) {
        let field = originalFields[fieldName];
        if (field.validation && Array.isArray(field.validation)) {
            field.required = field.validation.includes("required");
        }
    }
    return originalFields;
})

nextTick(() => {
    window.dataLayer = window.dataLayer || [];
})

function getCurrentForm() {
    return currentStepRef.value.formRef;
}

function loadStepFromUrl() {
    const url = new URL(window.location);
    let parsedStepNumber = Number.parseInt(
        url.searchParams.get("step")
    );
    currentStepNumber.value = parsedStepNumber || 1;
}

function addStepToUrl() {
    const url = new URL(window.location);
    url.searchParams.set("step", currentStepNumber.value);
    window.history.pushState({}, "", url);
}

const getFilePrefix = (stepId) => {
    const prefixes = {
        uploadID: 'proof-of-id',
        benefit: 'benefits',
        rightToWork: 'right-to-work',
        proofAddress: 'proof-of-address',
        euss: 'euss',
        selfie: 'selfie',
        benefitQuestion: 'benefits',
    }
    return prefixes[stepId];
}

function updateButtonColor($event) {
    isDisabled.value = !$event;
}

function setIsDisabled(value) {
    isDisabled.value = value;
}

function postFile(form, fileName) {
    // We're passing both question data and file binaries, so using form data.
    const data = new FormData();
    for (const [property, value] of Object.entries(form)) {
        // Rename the file to correspond to the step prefix and append loop index for numbering.
        if (property === "file") {
            const parts = value.name.split(".");
            const extension = parts[parts.length - 1];
            // Create a new file using the old, so we can rename.
            const file = new File(
                [value],
                `${fileName}.${extension}`,
                {type: value.type}
            );
            data.append('file', file);
        } else {
            data.append(property, value);
        }
    }
    
    const url = `/courses/${props.course}/doc-upload/${currentStep.value.id}`;
    return new Promise((resolve, reject) =>
        axios.post(url, data, {
            headers: {'content-type': 'multipart/form-data'}
        })
            .then((response) => resolve(response.data))
            .catch(err => {
                if (err?.response?.status === 413) {
                    getCurrentForm().errors.record({
                        file: 'Maximum file size is 10Mb, please reduce the size of your file.'
                    });
                }
                reject(err);
            })
    );

}

function onSubmit() {
    isLoading.value = true;

    formData[
        currentStepNumber.value - 1
    ] = getCurrentForm().data();

    if (formData[currentStepNumber.value - 1].file) {
        const files = Array.from(formData[currentStepNumber.value - 1].file);

        const filePrefix = getFilePrefix(currentStep.value.id);
        // Build promises for each file — a request for each file, so we're not sending too much data at once.
        const postFilePromises = files.map((file, index) => {
            const fileName = `${filePrefix}-${index + 1}`;

            const defaultFileData = (currentStepNumber.value === totalSteps)
                ? {file, lastFile: true}
                : {file};

            // Build the values we'll send for each request, filtering out the file property in favour of our mapped file.
 
            const singleFormData =
                Object
                    .entries(formData[0])
                    .reduce((acc, [property, value]) => property !== "file"
                            ? ({...acc, [property]: value})
                            : acc
                        , defaultFileData);

            return postFile(singleFormData, fileName);
        })

        // Build and return a single promise which will resolve once all requests are successful/reject if a request fails.
        return Promise.all(postFilePromises)
            .then(() => goForward())
            .catch((e) => {
                if (e?.response?.data?.errors) {
                    getCurrentForm().errors.record(e.response.data.errors);
                }
                if (benefitsFiles.value && benefitsFiles.value > 6) {
                    getCurrentForm().errors.errors.file.unshift('You can upload up to 6 files only');
                }
                scrollToFirstError();
            })
            .finally(() => (isLoading.value = false));
    } else {
        getCurrentForm()
            .post(`/courses/${props.course}/doc-upload/${currentStep.value.id}`)
            .then(goForward)
            .catch((e) => {
                    if (benefitsFiles.value && benefitsFiles.value > 6) {
                    getCurrentForm().errors.errors.file.unshift('You can upload up to 6 files only'); 
                }
                scrollToFirstError();
            })
            .finally(() => {
                isLoading.value = false;
            });
    }
}

function nextStep(step) {
    currentStepNumber.value = step;
    addStepToUrl();
    clearButtonState();
    scrollToBeginning();
}

function goForward() {
    isDisabled.value = true;

    if (currentStepNumber.value !== totalSteps) {
        window.dataLayer.push({'event': 'getInto_document_step_' + currentStepNumber.value + '_submitted'});
    } else {
        window.dataLayer.push({'event': 'getInto_document_form_completed'});
    }

    const question85Options = ['UK Passport', 'Biometric Residence Permit or Visa', 'UK driving license', 'Other country driving license', 'Irish, EEA or Swiss passport', 'Other country passport', 'Biometric or Immigration Status Document', 'Citizen card', 'Young Scot card'];
    
    const formStepData = formData[currentStepNumber.value - 1];
    
    if (currentStepNumber.value == 1 && (question85Options.indexOf(formStepData.Which_document) != -1)) {
        nextStep(3);
    } else if (currentStepNumber.value == 3 && ((formData[0].Which_document == 'UK Passport') || (formData[0].Which_document == 'Biometric Residence Permit or Visa') || (formData[0].Which_document == 'European Union Settlement Scheme'))) {
        nextStep(5);
    } else if (currentStepNumber.value == 5 && (formData[4].Benefit_Question == 'No I do not')) {
        nextStep(7);
    } else if (currentStepNumber.value < totalSteps) {
        nextStep(currentStepNumber.value + 1);
    } else {
        window.location.href = `/getinto-doc-upload-completed`;
    }
}

function scrollToBeginning() {
    const form = formRef.value;

    if (form) {
        const {top} = form.getBoundingClientRect();
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scrollTo(0, offsetY);
    }
}

function clearButtonState() {
    if (currentStepNumber.value === 2 || currentStepNumber.value === 3 || currentStepNumber.value === 4 || currentStepNumber.value === 5 || currentStepNumber.value === 6 || currentStepNumber.value === 7) {
        isDisabled.value = false;
    }
}

function handleError($event) {
    benefitsFiles.value = $event;
}

function scrollToFirstError() {
    const form = getCurrentForm();
    // If we don't have a form or errors, don't go any further.
    if (!form || !form.errors.any()) {
        return;
    }
    const error = document.getElementsByClassName('form-error')[0];
    if (error) {
        // Find the parent element (question) of the error class.
        const parent = error.parentNode;
        const {top} = parent.getBoundingClientRect();
        // Scroll to just above the parent element so we've got clear context.
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scrollTo({top: offsetY, behavior: 'smooth'});
    }
}
</script>
